import { DateTime } from 'luxon';
import InventoryItemStatusType from './enums/InventoryItemStatusType';

/**
 * Шаблоны оборудования и массовое создание: решения, которые можно проверить без браузера.
 *
 * Здесь всё, что отвечает на вопросы «сколько единиц получится», «какие серийники
 * не годятся», «до какого числа гарантия», «что именно уйдёт в запрос». Компоненты
 * остаются разметкой и запросами — так же устроены inventoryFiles.js и inventoryCard.js,
 * и по той же причине: формы в тестах не монтируются, а правила проверять нужно.
 *
 * ПРОВЕРКИ ЗДЕСЬ — НЕ ЗАЩИТА. Источник правды — бэкенд: InventoryItemService::bulkInsert()
 * отвергнет ту же партию и тогда, когда запрос придёт мимо интерфейса. Смысл клиентской
 * половины в том, чтобы человек увидел дубль серийника до отправки сорока единиц, а не
 * после отказа по всей партии.
 *
 * ГАРАНТИЯ СЧИТАЕТСЯ И ЗДЕСЬ, И НА СЕРВЕРЕ, и это не дублирование правила. Здесь —
 * предварительный показ под полем («Гарантия до 17.06.2027»), чтобы человек проверил
 * себя до сохранения. Записывает дату сервер: она учётная, по ней ищут просрочку,
 * и зависеть от часов и часового пояса браузера она не должна.
 */

/**
 * Статусы, с которыми технику можно завести. Зеркало InventoryItemsController::CREATION_STATUSES.
 *
 * Ремонт, неисправность и списание — действия в карточке с обязательной причиной,
 * а «Списано» ещё и необратимо: действий из него нет.
 */
export const CREATION_STATUSES = [
    InventoryItemStatusType.IN_STOCK,
    InventoryItemStatusType.RESERVED,
    InventoryItemStatusType.IN_USE,
];

/**
 * Статус заведения из значения шаблона. Недопустимое или пустое — «На складе»: форма
 * не должна открываться со значением, которого нет в списке и которое сервер отвергнет.
 *
 * @param {*} value default_status шаблона.
 * @returns {number}
 */
export function creationStatus(value) {
    const status = value === null || value === undefined || value === '' ? NaN : Number(value);

    return CREATION_STATUSES.includes(status) ? status : InventoryItemStatusType.IN_STOCK;
}

/**
 * Сценарий владения. Зеркало App\Enums\InventoryOwnerType.
 */
export const OWNER_TYPE = {
    EMPLOYEE: 'employee',
    EXTERNAL: 'external',
};

/**
 * Подписи сценариев владения.
 *
 * СКЛАДА ЗДЕСЬ НЕТ, и это исправление 7-D1. В 7-D был третий вариант «Склад», и он был
 * ошибкой по смыслу: склад — МЕСТО, где лежит ничейная техника, а не тот, кто ею владеет.
 * «Владельца нет» — это отсутствие выбора (null) вместе со статусом «На складе», а не
 * особое значение. Поступление на склад теперь вообще не требует выбирать владельца.
 */
export const OWNER_TYPE_OPTIONS = [
    { value: OWNER_TYPE.EMPLOYEE, label: 'Сотрудник Талан' },
    { value: OWNER_TYPE.EXTERNAL, label: 'Внешний' },
];

/**
 * Вид техники. Зеркало App\Enums\InventoryEquipmentKind и констант inventoryPreview.js.
 */
export const EQUIPMENT_KIND = {
    LAPTOP: 'laptop',
    MONITOR: 'monitor',
    HUB: 'hub',
    DESKTOP: 'desktop',
    PRINTER: 'printer',
    PHONE: 'phone',
    OTHER: 'other',
};

export const EQUIPMENT_KIND_OPTIONS = [
    { value: EQUIPMENT_KIND.LAPTOP, label: 'Ноутбук' },
    { value: EQUIPMENT_KIND.MONITOR, label: 'Монитор' },
    { value: EQUIPMENT_KIND.DESKTOP, label: 'Системный блок' },
    { value: EQUIPMENT_KIND.HUB, label: 'Хаб или док-станция' },
    { value: EQUIPMENT_KIND.PRINTER, label: 'Принтер или МФУ' },
    { value: EQUIPMENT_KIND.PHONE, label: 'Телефон' },
    { value: EQUIPMENT_KIND.OTHER, label: 'Прочее' },
];

/**
 * Срок гарантии по умолчанию, месяцев. Столько даёт большинство поставщиков.
 */
export const DEFAULT_WARRANTY_MONTHS = 12;

/**
 * Потолок размера партии.
 *
 * Не техническое ограничение, а смысловое: партия — это то, что человек проверяет
 * глазами по накладной. Сотня строк серийников в одной форме уже не проверяется,
 * и такую поставку заводят несколькими партиями.
 */
export const MAX_BATCH_SIZE = 100;

/**
 * Коды состояний серийного номера в списке.
 */
export const SERIAL_OK = 'ok';
export const SERIAL_EMPTY = 'empty';
export const SERIAL_DUPLICATE = 'duplicate';

/**
 * Состояние списка серийных номеров относительно нужного количества.
 *
 * Возвращает и разметку каждой строки, и общий итог: интерфейсу нужно и подсветить
 * конкретную ячейку, и решить, давать ли нажать «Создать».
 *
 * Сравнение регистронезависимое, как и на сервере: «p12345» и «P12345» — один и тот же
 * номер на коробке, и пропустить оба значит завести дубль, который с виду разный.
 *
 * @param {string[]} serials Номера по позициям партии; длина может не совпадать с count.
 * @param {number} count Сколько единиц создаётся.
 * @returns {{states: string[], duplicates: string[], filled: number, missing: number,
 *            isValid: boolean}}
 */
export function analyzeSerialNumbers(serials, count) {
    const list = Array.isArray(serials) ? serials : [];
    const size = Number(count) > 0 ? Number(count) : 0;

    const seen = {};
    const duplicates = [];

    // Счёт ведётся по всему присланному списку, а не по первым size позициям: дубль
    // в лишней строке — это тоже дубль, и прятать его до удаления лишнего незачем.
    list.forEach((value) => {
        const key = String(value ?? '')
            .trim()
            .toLowerCase();

        if (key === '') {
            return;
        }

        if (seen[key]) {
            if (duplicates.indexOf(key) === -1) {
                duplicates.push(key);
            }

            return;
        }

        seen[key] = true;
    });

    const states = [];

    for (let index = 0; index < size; index += 1) {
        const value = String(list[index] ?? '').trim();

        if (value === '') {
            states.push(SERIAL_EMPTY);

            continue;
        }

        states.push(duplicates.indexOf(value.toLowerCase()) === -1 ? SERIAL_OK : SERIAL_DUPLICATE);
    }

    const filled = states.filter((state) => state !== SERIAL_EMPTY).length;

    return {
        states,
        duplicates,
        filled,
        // Избытка здесь больше нет: полей ровно столько, сколько единиц, и лишнему номеру
        // просто некуда попасть. Он возникал только у вставки списком, а её убрали
        // в 7-D1 — вместе с необходимостью объяснять человеку, что часть вставленного
        // не поместилась.
        missing: Math.max(0, size - filled),
        isValid: size > 0 && filled === size && duplicates.length === 0,
    };
}

/**
 * Дата окончания гарантии для показа под полем.
 *
 * Тот же расчёт, что делает сервер (InventoryItemService::resolveWarrantyExpiry):
 * прибавление месяцев, а не дней. У месяцев разная длина, и «12 месяцев» от 29 февраля
 * должны дать 28 февраля, а не случайное число в марте — luxon это делает сам.
 *
 * @param {string} deliveredAt Дата поставки, ISO (yyyy-MM-dd).
 * @param {number} months Срок гарантии в месяцах.
 * @returns {string} Дата в формате yyyy-MM-dd или пустая строка, если считать не от чего.
 */
export function warrantyExpiresAt(deliveredAt, months) {
    const source = String(deliveredAt ?? '').trim();
    const count = Number(months);

    if (source === '' || !Number.isFinite(count) || count <= 0) {
        return '';
    }

    const date = DateTime.fromISO(source);

    if (!date.isValid) {
        return '';
    }

    return date.plus({ months: count }).toFormat('yyyy-MM-dd');
}

/**
 * Та же дата человеку: «Гарантия до 17.06.2027».
 *
 * @param {string} deliveredAt
 * @param {number} months
 * @returns {string} Пустая строка, если считать не от чего, — подпись просто не рисуется.
 */
export function warrantyHint(deliveredAt, months) {
    const expires = warrantyExpiresAt(deliveredAt, months);

    if (expires === '') {
        return '';
    }

    return `Гарантия до ${DateTime.fromISO(expires).toFormat('dd.MM.yyyy')}`;
}

/**
 * Системные поля формы создания.
 *
 * Их спрашивает сама форма своими полями — серийный номер у каждой единицы, город
 * и юр. лицо общие на партию, — поэтому отдельным полем из шаблона они не рисуются:
 * иначе одно и то же спрашивалось бы дважды. Зеркало InventoryPresetService::SYSTEM_KEYS.
 */
export const SYSTEM_FIELD = {
    SERIAL_NUMBER: 'serial_number',
    LOCATION_CITY: 'location_city',
    LEGAL_ENTITY: 'legal_entity',
};

/**
 * Заготовка формы создания из шаблона.
 *
 * ШАБЛОН ОПИСЫВАЕТ ФОРМУ, А НЕ ТОЛЬКО ЗНАЧЕНИЯ. До 7-D2 атрибуты шаблона молча
 * копировались в каждую создаваемую единицу: человек их не видел и поправить не мог,
 * а незаполненный атрибут ломал создание целиком. Теперь каждый включённый атрибут —
 * это поле формы: с подписью, со значением по умолчанию и с признаком обязательности.
 *
 * ШАБЛОН ДАЁТ ТИП, А НЕ ПАРТИЮ. Отсюда не приходят ни цена, ни дата поставки, ни город,
 * ни серийный номер: это свойства конкретной поставки и конкретного экземпляра.
 *
 * АТРИБУТ ЕСТЬ В ШАБЛОНЕ — ЗНАЧИТ, ОН В ФОРМЕ. Третьего состояния нет: признака
 * «включён/выключен» у атрибута не существует, и выключенных строк, про которые надо
 * помнить, что они не работают, не бывает. Убрали из шаблона — исчезло из формы.
 *
 * @param {Object|null} preset
 * @param {function(string): (string|null)} [systemKeyOf] Системный смысл типа или null;
 *        передаётся снаружи, чтобы модуль не знал названий справочников.
 * @returns {Object} Заготовка общих полей партии вместе с полями из шаблона.
 */
export function templateDefaults(preset, systemKeyOf = () => null) {
    const source = preset && typeof preset === 'object' ? preset : {};
    const attributes = Array.isArray(source.attributes) ? source.attributes : [];

    const fields = [];
    let legalEntityDefault = null;

    // Порядок задаёт шаблон. При равных позициях решает порядок заведения (id) —
    // тот же, в каком строки пришли: сортировка обязана быть устойчивой, иначе поля
    // формы менялись бы местами от открытия к открытию.
    const ordered = attributes
        .map((attribute, index) => ({ attribute, index }))
        .sort((left, right) => {
            const byPosition = Number(left.attribute?.position ?? 0) - Number(right.attribute?.position ?? 0);

            return byPosition !== 0 ? byPosition : left.index - right.index;
        })
        .map((entry) => entry.attribute);

    ordered.forEach((attribute) => {
        const systemKey = systemKeyOf(attribute?.type);

        if (systemKey === SYSTEM_FIELD.LEGAL_ENTITY) {
            // Юр. лицо у формы своё поле-справочник: из шаблона берём только значение,
            // чтобы подставить его туда заранее.
            legalEntityDefault = String(attribute?.value ?? '').trim() || null;

            return;
        }

        // Серийный номер и город из шаблона полем не становятся: их спрашивает форма
        // сама, и второе поле с тем же смыслом сбивало бы с толку.
        if (systemKey) {
            return;
        }

        fields.push({
            type: attribute.type,
            attribute_id: attribute.attribute_id ?? null,
            attribute_type: attribute.attribute_type ?? attribute.type,
            value_type: attribute.value_type ?? 'string',
            value: attribute.value ?? '',
            required: Boolean(attribute.required),
            position: Number(attribute.position ?? 0),
            searchable: Boolean(attribute.searchable),
        });
    });

    return {
        preset_id: source.id ?? null,
        name: source.name ?? '',
        kind: source.kind ?? null,
        status: creationStatus(source.default_status),
        warranty_months: Number(source.warranty_months) > 0 ? Number(source.warranty_months) : DEFAULT_WARRANTY_MONTHS,
        // null — «владелец не назначен», и это законный ответ, а не пробел:
        // поступившая на склад техника ничья, пока её кому-то не выдали.
        owner_type: source.owner_type ?? null,
        fields,
        legal_entity_default: legalEntityDefault,
    };
}

/**
 * Владелец в том виде, в каком его ждёт бэкенд.
 *
 * ЗАПОЛНЕНА НЕ БОЛЬШЕ ЧЕМ ОДНА ССЫЛКА — тот же инвариант, что держит
 * InventoryItemService::assignOwner(). Здесь он повторён не ради защиты, а чтобы форма
 * не отправляла мусор: человек выбрал «Сотрудник», потом передумал на «Склад» —
 * выбранный ранее сотрудник не должен уехать в запрос.
 *
 * @param {string} ownerType
 * @param {{responsiblePersonId?: number|null, holderId?: number|null}} selection
 * @returns {{owner_type: string, responsible_person_id: number|null, holder_id: number|null}}
 */
export function ownerPayload(ownerType, selection = {}) {
    if (ownerType === OWNER_TYPE.EMPLOYEE) {
        return {
            owner_type: ownerType,
            responsible_person_id: selection.responsiblePersonId ?? null,
            holder_id: null,
        };
    }

    if (ownerType === OWNER_TYPE.EXTERNAL) {
        return {
            owner_type: ownerType,
            responsible_person_id: null,
            holder_id: selection.holderId ?? null,
        };
    }

    // ВЛАДЕЛЕЦ НЕ НАЗНАЧЕН. Обе ссылки пусты, и сам вид тоже пуст: это не «склад»
    // и не значение по умолчанию, а отсутствие выбора — законное состояние техники,
    // которая поступила и пока никому не выдана.
    return { owner_type: null, responsible_person_id: null, holder_id: null };
}

/**
 * Нужен ли при этом сценарии дополнительный выбор и какой.
 *
 * @param {string} ownerType
 * @returns {{employee: boolean, holder: boolean}}
 */
export function ownerSelectionFields(ownerType) {
    return {
        employee: ownerType === OWNER_TYPE.EMPLOYEE,
        holder: ownerType === OWNER_TYPE.EXTERNAL,
    };
}

/**
 * Поля формы, заполненные человеком, в виде атрибутов запроса.
 *
 * ПУСТОЕ ПОЛЕ АТРИБУТА НЕ СОЗДАЁТ. Необязательное поле, которое оставили пустым, —
 * это ответ «нечего сказать», а не значение: строка справочника без значения не говорит
 * о предмете ничего и только засоряет карточку. Обязательные пустыми сюда не доходят —
 * их не пускает validateBatch().
 *
 * @param {Array} fields
 * @returns {Array}
 */
export function attributesFromFields(fields) {
    const list = Array.isArray(fields) ? fields : [];

    return list
        .filter((field) => String(field?.value ?? '').trim() !== '')
        .map((field) => ({
            attribute_id: field.attribute_id ?? null,
            attribute_type: field.attribute_type ?? field.type,
            type: field.type,
            value: String(field.value).trim(),
            value_type: field.value_type ?? 'string',
            searchable: Boolean(field.searchable),
        }));
}

/**
 * Какие плановые сроки владения положены статусу заведения.
 *
 * Зеркало InventoryItemService::guardOwnership(): «Владеет с (план)» — только у резерва
 * (у выданного этот план уже исполнен), «Вернуть до» — у резерва и выданного. На складе
 * обещать некому.
 *
 * @param {number|null|undefined} status
 * @returns {{expected: boolean, until: boolean}}
 */
export function plannedDateFields(status) {
    const value = Number(status ?? InventoryItemStatusType.IN_STOCK);

    return {
        expected: value === InventoryItemStatusType.RESERVED,
        until: value === InventoryItemStatusType.RESERVED || value === InventoryItemStatusType.IN_USE,
    };
}

/**
 * Собрать партию в массив позиций для POST /inventory/items/bulk-insert.
 *
 * ОДИН ЗАПРОС НА ВСЮ ПАРТИЮ. Раньше форма отправляла массив одинаковых копий, а потом
 * досылала держателя отдельным запросом на каждую созданную единицу — партия из сорока
 * штук стоила сорока лишних обращений. Теперь владелец едет вместе с позицией.
 *
 * Общие поля копируются каждой единице, индивидуальные подставляются по позиции.
 * Серийный номер добавляется отдельным атрибутом: у предмета он живёт значением
 * справочника, а не колонкой.
 *
 * @param {Object} common Общие поля: name, city_id, cost, delivered_at, warranty_months,
 *        status, owner_type, responsible_person_id, holder_id, attributes.
 * @param {Array<{serial: string, responsible_person_id?: number|null, comment?: string}>} units
 * @param {{type: string, attribute_id?: number|null, value_type?: string}} serialAttribute
 *        Описание справочника серийных номеров: его название и, если есть, строка-значение.
 * @returns {Array<Object>}
 */
export function buildBatchPayload(common, units, serialAttribute) {
    const list = Array.isArray(units) ? units : [];
    const shared = common && typeof common === 'object' ? common : {};
    const sharedAttributes = attributesFromFields(shared.fields);
    // Дата, введённая до смены статуса, в запрос не уходит: сервер её бы отверг,
    // а человек её уже не видит.
    const dates = plannedDateFields(shared.status);

    return list.map((unit) => {
        const attributes = sharedAttributes.slice();
        const serial = String(unit?.serial ?? '').trim();

        if (serial !== '' && serialAttribute && serialAttribute.type) {
            attributes.push({
                attribute_id: serialAttribute.attribute_id ?? null,
                attribute_type: serialAttribute.type,
                type: serialAttribute.type,
                value: serial,
                value_type: serialAttribute.value_type ?? 'string',
                // Серийный номер участвует в поиске по списку — это его главное
                // назначение помимо учёта.
                searchable: true,
            });
        }

        return {
            // Шаблон едет вместе с позицией: по нему сервер проверяет обязательные поля
            // шаблона сам, не полагаясь на то, что запрос пришёл именно из этой формы.
            preset_id: shared.preset_id ?? null,
            name: shared.name ?? '',
            city_id: shared.city_id ?? null,
            cost: shared.cost ?? 0,
            delivered_at: shared.delivered_at || null,
            warranty_months: shared.warranty_months ?? DEFAULT_WARRANTY_MONTHS,
            status: shared.status ?? 0,
            ...ownerPayload(shared.owner_type, {
                // Ответственный может отличаться у каждой единицы: одну партию ноутбуков
                // раздают разным людям. Общий используется, пока у позиции своего нет.
                responsiblePersonId: unit?.responsible_person_id ?? shared.responsible_person_id ?? null,
                holderId: shared.holder_id ?? null,
            }),
            holder_expected_at: (dates.expected && shared.holder_expected_at) || null,
            holder_until_at: (dates.until && shared.holder_until_at) || null,
            attributes,
        };
    });
}

/**
 * Названия обязательных полей шаблона, оставшихся пустыми.
 *
 * Отдельной функцией, а не условием внутри проверки: тот же список нужен форме, чтобы
 * подсветить сами поля, а не только назвать их в списке причин внизу.
 *
 * @param {Array} fields
 * @returns {string[]}
 */
export function requiredFieldsMissing(fields) {
    const list = Array.isArray(fields) ? fields : [];

    return list
        .filter((field) => field?.required && String(field?.value ?? '').trim() === '')
        .map((field) => String(field.type ?? ''));
}

/**
 * Ошибки общих полей формы: имя поля → текст под ним.
 *
 * КАЖДАЯ ОШИБКА ЗНАЕТ СВОЁ ПОЛЕ, и это главное изменение 7-D3. Раньше те же правила
 * возвращались плоским списком причин под формой: «Выберите город» приходилось
 * сопоставлять с одним из десяти полей глазами, а на телефоне список ещё и уезжал
 * за пределы экрана — человек видел погашенную кнопку и не видел, почему.
 *
 * Список причин остался ровно для того, что к полю не привязывается (см. batchReasons).
 *
 * @param {Object} common Общие поля партии.
 * @returns {Object} Карта «имя поля → текст».
 */
export function batchFieldValidation(common) {
    const shared = common && typeof common === 'object' ? common : {};
    const errors = {};

    if (!String(shared.name ?? '').trim()) {
        errors.name = 'Укажите наименование или модель.';
    }

    if (!shared.city_id) {
        errors.city_id = 'Выберите город.';
    }

    if (!shared.legal_entity_id) {
        errors.legal_entity_id = 'Укажите юридическое лицо.';
    }

    if (!Number.isFinite(Number(shared.cost))) {
        errors.cost = 'Укажите стоимость за единицу.';
    }

    // Колонка inventory_items.delivered_at объявлена NOT NULL: без даты партия
    // не сохранится в принципе, и узнать об этом из отказа сервера — худший вариант.
    if (!String(shared.delivered_at ?? '').trim()) {
        errors.delivered_at = 'Укажите дату поставки.';
    }

    // Пустое значение проверяется до Number(): Number(null) и Number('') дают 0,
    // то есть «гарантии нет» было бы неотличимо от «поле не заполнили».
    const months = shared.warranty_months;

    if (months === null || months === undefined || months === '' || !Number.isFinite(Number(months)) || months < 0) {
        errors.warranty_months = 'Укажите гарантию в месяцах.';
    }

    // Владелец необязателен: техника может поступить ничьей. Но если сценарий выбран,
    // выбор должен быть завершён — «внешний» без держателя это половина ответа.
    if (shared.owner_type === OWNER_TYPE.EXTERNAL && !shared.holder_id) {
        errors.holder_id = 'Выберите держателя.';
    }

    if (shared.owner_type === OWNER_TYPE.EMPLOYEE && !shared.responsible_person_id) {
        errors.responsible_person_id = 'Выберите сотрудника.';
    }

    // Статус и владелец должны сходиться — зеркало InventoryItemService::guardOwnership().
    // «На складе» с владельцем или «В эксплуатации» без него — карточка, на которой
    // «Выдать» и «Сменить владельца» отказывают оба. Пустой статус — это «На складе».
    const status = Number(shared.status ?? InventoryItemStatusType.IN_STOCK);
    const hasOwner = shared.owner_type === OWNER_TYPE.EMPLOYEE || shared.owner_type === OWNER_TYPE.EXTERNAL;

    if (status === InventoryItemStatusType.IN_STOCK && hasOwner) {
        errors.status = 'На складе техника ни за кем не числится: уберите владельца или выберите другой статус.';
    }

    if (status === InventoryItemStatusType.IN_USE && !hasOwner) {
        errors.status = 'Техника в эксплуатации должна числиться за владельцем: выберите его.';
    }

    const dates = plannedDateFields(shared.status);

    if (dates.expected && dates.until && shared.holder_expected_at && shared.holder_until_at) {
        if (String(shared.holder_until_at) < String(shared.holder_expected_at)) {
            errors.holder_until_at = 'Срок возврата не может быть раньше плановой даты выдачи.';
        }
    }

    return errors;
}

/**
 * Ошибки полей шаблона: МЕСТО поля в списке → текст.
 *
 * По месту, а не по названию: два поля с одинаковой подписью — вырожденный случай,
 * но подсветить в нём оба вместо одного было бы прямой ложью о том, где исправлять.
 *
 * @param {Array} fields
 * @returns {Object}
 */
export function batchTemplateFieldErrors(fields) {
    const list = Array.isArray(fields) ? fields : [];
    const errors = {};

    list.forEach((field, index) => {
        if (field?.required && String(field?.value ?? '').trim() === '') {
            errors[index] = 'Заполните это поле.';
        }
    });

    return errors;
}

/**
 * Ошибки серийных номеров: позиция → текст.
 *
 * Пустота и дубль — разные беды и исправляются по-разному, поэтому текст разный.
 * Для одной единицы номер позиции в тексте не называется: называть нечего.
 *
 * @param {string[]} serials
 * @param {number} count
 * @returns {Object}
 */
export function batchSerialErrors(serials, count) {
    const analysis = analyzeSerialNumbers(serials, count);
    const errors = {};

    analysis.states.forEach((state, index) => {
        if (state === SERIAL_DUPLICATE) {
            errors[index] = 'Такой номер уже есть в партии.';

            return;
        }

        if (state === SERIAL_EMPTY) {
            errors[index] = 'Укажите серийный номер.';
        }
    });

    return errors;
}

/**
 * Причины, которые нельзя привязать ни к одному полю формы.
 *
 * Сейчас такая ровно одна — количество единиц: его задаёт предыдущий шаг, и поля
 * для него в этой форме нет вовсе. Именно для таких случаев и остаётся общий блок.
 *
 * @param {number} count
 * @returns {string[]}
 */
export function batchReasons(count) {
    const size = Number(count);

    if (!Number.isFinite(size) || size < 1 || size > MAX_BATCH_SIZE) {
        return [`Количество должно быть от 1 до ${MAX_BATCH_SIZE}.`];
    }

    return [];
}

/**
 * Готова ли партия к отправке.
 *
 * Отдельная функция, а не набор условий в шаблоне: список обязательных полей — это
 * правило, и оно должно проверяться тестом, а не читаться по кусочкам из трёх v-if.
 *
 * Собрана из тех же проверок, что раскладываются по полям: два списка одних и тех же
 * правил однажды разошлись бы, и форма стала бы отвергать то, на что не жалуется.
 *
 * @param {Object} common
 * @param {string[]} serials
 * @param {number} count
 * @returns {{isValid: boolean, reasons: string[]}}
 */
export function validateBatch(common, serials, count) {
    const shared = common && typeof common === 'object' ? common : {};
    const fields = batchFieldValidation(shared);
    const reasons = [];

    // Порядок причин сохранён прежним: он же порядок полей в форме.
    const order = [
        'name',
        'city_id',
        'cost',
        'delivered_at',
        'legal_entity_id',
        'warranty_months',
        'status',
        'holder_id',
        'holder_until_at',
    ];

    order.forEach((field) => {
        if (fields[field]) {
            reasons.push(fields[field]);
        }
    });

    if (fields.responsible_person_id) {
        reasons.push(fields.responsible_person_id);
    }

    // Поля, пришедшие из шаблона. Обязательность задаёт шаблон, а не форма: у одного
    // типа техники поставщик обязателен, у другого — нет, и зашивать это в форму нельзя.
    requiredFieldsMissing(shared.fields).forEach((title) => {
        reasons.push(`Укажите «${title}».`);
    });

    batchReasons(count).forEach((reason) => reasons.push(reason));

    const serialState = analyzeSerialNumbers(serials, count);

    if (serialState.missing > 0) {
        reasons.push(`Заполните серийные номера: не хватает ${serialState.missing}.`);
    }

    if (serialState.duplicates.length > 0) {
        reasons.push('Серийные номера повторяются.');
    }

    return { isValid: reasons.length === 0, reasons };
}

/**
 * Человеческие названия полей позиции — для текста ошибки.
 *
 * Ключи совпадают с именами полей в запросе: ответ сервера приходит именно ими
 * («0.name», «2.attributes.0.value»), и таблица соответствия здесь одна на всё.
 */
const FIELD_TITLES = {
    name: 'наименование',
    city_id: 'город',
    cost: 'стоимость',
    delivered_at: 'дату поставки',
    warranty_months: 'гарантию в месяцах',
    holder_id: 'держателя',
    responsible_person_id: 'сотрудника',
    status: 'статус',
};

/**
 * Превратить ответ 422 в понятные человеку строки.
 *
 * ЗАЧЕМ ЭТО ВООБЩЕ НУЖНО. Laravel отдаёт ошибки ключами вида «0.attributes.0.value»
 * и текстом из языкового файла. Русского файла валидации в проекте нет, поэтому текст
 * приходит сырым ключом — пользователь видел буквально «validation.required» и не мог
 * понять ни что не так, ни где. Показывать такое нельзя никогда.
 *
 * ПОЗИЦИЯ НАЗЫВАЕТСЯ НОМЕРОМ. В партии из сорока единиц «укажите серийный номер»
 * без номера позиции — это предложение искать вручную. Индексы у сервера с нуля,
 * у человека с единицы.
 *
 * Незнакомый ключ не выбрасывается, а превращается в общую фразу с номером позиции:
 * потерять ошибку хуже, чем показать её обобщённо.
 *
 * @param {Object} errors Объект errors из ответа 422.
 * @param {function(string): boolean} [isSerialField] Является ли путь серийным номером.
 * @returns {string[]} Готовые строки, по одной на ошибку.
 */
/**
 * Текст ошибки, если он написан для человека, и пустая строка иначе.
 *
 * ГРАНИЦА ПРОХОДИТ ПО ТОЧКЕ. Непереведённое правило приезжает ключом языкового файла —
 * «validation.required», «validation.date», — и показать его значит показать
 * пользователю внутренности фреймворка. Всё остальное сервер пишет словами
 * и намеренно: там названы и поле, и причина.
 *
 * @param {*} message Значение ключа errors: строка или массив строк.
 * @returns {string}
 */
function humanMessage(message) {
    const first = Array.isArray(message) ? message[0] : message;
    const text = String(first ?? '').trim();

    return text === '' || text.startsWith('validation.') ? '' : text;
}

export function describeBatchErrors(errors, isSerialField = () => false) {
    const source = errors && typeof errors === 'object' ? errors : {};

    return Object.keys(source).map((key) => {
        const parts = key.split('.');
        const position = Number(parts[0]);
        const unit = Number.isFinite(position) ? position + 1 : null;
        const written = humanMessage(source[key]);

        // Сервер умеет отвечать и по-человечески — обязательными полями шаблона,
        // дублем серийного номера. Такой текст точнее любого, который можно собрать
        // из ключа: в нём есть название поля, которого в ключе нет.
        if (written !== '') {
            return unit ? `${written} (позиция ${unit})` : written;
        }

        // Ошибка на атрибуте: серийный номер называем по имени, остальные — как значение
        // справочника. Какой именно справочник, решает вызывающий: названий типов
        // этот модуль не знает.
        if (parts[1] === 'attributes') {
            const title = isSerialField(key) ? 'серийный номер' : 'значение справочника';

            return unit ? `Укажите ${title} для позиции ${unit}` : `Укажите ${title}`;
        }

        const field = parts[1];

        if (field && FIELD_TITLES[field]) {
            return unit && unit > 1
                ? `Укажите ${FIELD_TITLES[field]} для позиции ${unit}`
                : `Укажите ${FIELD_TITLES[field]}`;
        }

        return unit ? `Проверьте данные позиции ${unit}` : 'Проверьте заполненные данные';
    });
}

/**
 * Ошибки, привязанные к конкретному общему полю формы.
 *
 * Общие поля одинаковы у всех позиций, поэтому ошибка по любой из них — это ошибка
 * одного поля формы, и показывать её надо рядом с ним, а не списком внизу.
 *
 * @param {Object} errors Объект errors из ответа 422.
 * @returns {Object} Карта «имя поля → текст».
 */
export function batchFieldErrors(errors) {
    const source = errors && typeof errors === 'object' ? errors : {};
    const result = {};

    Object.keys(source).forEach((key) => {
        const field = key.split('.')[1];

        if (field && FIELD_TITLES[field] && !result[field]) {
            result[field] = `Укажите ${FIELD_TITLES[field]}`;
        }
    });

    return result;
}

/**
 * Ошибки серийных номеров, привязанные к позициям партии: позиция → текст.
 *
 * КЛЮЧ ИМЕННО «N.serial_number». Так отвечает InventoryItemService::guardSerialNumbers()
 * и guardSerialNumbersAreFree() — пустой номер, повтор внутри партии, номер, уже занятый
 * другой единицей. Раньше здесь ловилось «N.attributes», и это было мимо: под этим ключом
 * сервер отвечает про ПОЛЯ ШАБЛОНА, а настоящая ошибка серийника — «Серийный номер
 * позиции 3 обязателен» — не доходила до своей строки вовсе и оседала в общем списке
 * внизу, ровно там, откуда её и убирали.
 *
 * @param {Object} errors Объект errors из ответа 422.
 * @returns {Object} Карта «индекс позиции → текст».
 */
export function batchUnitErrors(errors) {
    const source = errors && typeof errors === 'object' ? errors : {};
    const result = {};

    Object.keys(source).forEach((key) => {
        const parts = key.split('.');
        const position = Number(parts[0]);

        if (!Number.isFinite(position) || parts[1] !== 'serial_number') {
            return;
        }

        if (!result[position]) {
            // Написанный сервером текст точнее: он называет сам номер и, если тот занят,
            // чужую единицу. Общая фраза остаётся на случай непереведённого правила.
            result[position] = humanMessage(source[key]) || 'Проверьте серийный номер';
        }
    });

    return result;
}

/**
 * Имя поля, названное сервером в кавычках-ёлочках.
 *
 * Отказ по обязательному полю шаблона сервер пишет словами и с названием поля:
 * «Поле «Поставщик» обязательно по шаблону — заполните его.». Ключ ошибки при этом —
 * «N.attributes», без указания, какое из полей виновато: атрибуты уезжают массивом,
 * и пустые в него не попадают вовсе, так что место в массиве ничего не говорит.
 * Название — единственное, по чему ошибку можно вернуть на её поле.
 *
 * @param {string} message
 * @returns {string}
 */
function quotedFieldName(message) {
    const match = /«([^»]+)»/.exec(String(message ?? ''));

    return match ? match[1].trim().toLowerCase() : '';
}

/**
 * Подпись поля в виде, пригодном для сравнения с названием из сообщения сервера.
 *
 * @param {Object} field
 * @returns {string}
 */
function fieldTitleKey(field) {
    return String(field?.type ?? '')
        .trim()
        .toLowerCase();
}

/**
 * Ошибки полей шаблона, пришедшие от сервера: место поля в списке → текст.
 *
 * Поля шаблона одинаковы у всех позиций партии — они общие, — поэтому ошибка по любой
 * из позиций это ошибка одного поля формы.
 *
 * @param {Object} errors Объект errors из ответа 422.
 * @param {Array} fields Поля шаблона в том порядке, в каком их видит человек.
 * @returns {Object} Карта «место поля → текст».
 */
export function batchServerTemplateFieldErrors(errors, fields) {
    const source = errors && typeof errors === 'object' ? errors : {};
    const list = Array.isArray(fields) ? fields : [];
    const result = {};

    Object.keys(source).forEach((key) => {
        if (key.split('.')[1] !== 'attributes') {
            return;
        }

        const name = quotedFieldName(humanMessage(source[key]));

        if (name === '') {
            return;
        }

        const index = list.findIndex((field) => fieldTitleKey(field) === name);

        if (index !== -1 && !result[index]) {
            result[index] = humanMessage(source[key]);
        }
    });

    return result;
}

/**
 * Ключи ответа, которые удалось разложить по полям формы.
 *
 * Нужны, чтобы общий блок внизу показал ТОЛЬКО неразложенное: одна и та же ошибка,
 * прочитанная дважды, заставляет искать второе место, где надо что-то исправить.
 *
 * @param {Object} errors
 * @param {Array} fields Поля шаблона.
 * @returns {string[]} Ключи ответа, оставшиеся без своего поля.
 */
export function batchUnplacedErrors(errors, fields) {
    const source = errors && typeof errors === 'object' ? errors : {};
    const list = Array.isArray(fields) ? fields : [];
    const placedFields = batchFieldErrors(source);

    return Object.keys(source).filter((key) => {
        const parts = key.split('.');

        if (parts[1] === 'serial_number') {
            return false;
        }

        if (parts[1] === 'attributes') {
            const name = quotedFieldName(humanMessage(source[key]));

            return name === '' || list.findIndex((field) => fieldTitleKey(field) === name) === -1;
        }

        return !placedFields[parts[1]];
    });
}
